import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';
import { toSafeString } from '~/utils';

import { PICKUP_SHIPPING_TYPES } from '~/constants';
import { RECEIVE } from '~/constants/errorCode';

import type {
  TypeApiResponse,
  TypeInitialPickupData,
  TypePickupData,
  TypePickupReleaseInfo,
  TypePickupReleaseInfoWithId,
  TypePutReceivingRequestParams,
  TypePutReceivingRequestResponse,
  TypeReceiveDetailOrderItem,
  TypeReceiveRequestDetailModalFilter,
} from '~/types/receive.types';

type UpdateRequestParams = {
  ownerCode: string;
  orderId: number;
  filterData: TypeReceiveRequestDetailModalFilter;
  initialPickupData: TypeInitialPickupData;
  tableData: TypeReceiveDetailOrderItem[];
  deletedTableData: TypeReceiveDetailOrderItem[];
};

const formatOrderItem = (item: TypeReceiveDetailOrderItem) => ({
  id: item.itemId,
  goodsId: item.goodsId,
  dateUseYn: item.dateUseYn,
  ...(item.dateType === 'expirationDate' && { expirationDate: item.date }),
  ...(item.dateType === 'manufactureDate' && { manufactureDate: item.date }),
  usePallet: item.usePallet,
  lotNo: item.lotNo || null,
  boxQty: item.boxQty,
  boxPerQty: item.boxPerQty,
  eachQty: item.eachQty,
  modificationType: item.modificationType,
});

const isPickupDataModified = (initial: TypePickupReleaseInfo, current: TypePickupData): boolean => {
  return (
    toSafeString(initial.zipNo) !== toSafeString(current.zipCode) ||
    toSafeString(initial.address) !== toSafeString(current.address) ||
    toSafeString(initial.detailAddress) !== toSafeString(current.detailAddress) ||
    toSafeString(initial.phoneNo) !== toSafeString(current.contactNumber) ||
    toSafeString(initial.vehicleSizeCode) !== toSafeString(current.vehicleSize?.value) ||
    toSafeString(initial.palletQty) !== toSafeString(current.palletQuantity) ||
    toSafeString(initial.memo) !== toSafeString(current.memo)
  );
};

const createPickupInfo = (
  data: TypePickupData,
  defaultOutboundYn: boolean,
  modificationType: 'NEW' | 'MODIFY' | 'DELETE',
  id?: number
): TypePutReceivingRequestParams['pickupReleaseInfos'][number] => ({
  ...(id !== undefined && { id }),
  defaultOutboundYn,
  modificationType,
  ...(modificationType !== 'DELETE' && {
    zipNo: data.zipCode,
    address: data.address,
    detailAddress: data.detailAddress,
    phoneNo: data.contactNumber,
    ...(data.isUseVehicleSize && data.vehicleSize?.value && { vehicleSizeCode: data.vehicleSize?.value }),
    palletQty: data.palletQuantity,
    memo: data.memo,
  }),
});

const processPickupAddressUpdate = (
  initial: TypePickupReleaseInfoWithId | undefined,
  current: TypePickupData | undefined,
  isSelected: boolean,
  defaultOutboundYn: boolean
) => {
  if (initial && !isSelected) {
    return createPickupInfo({} as TypePickupData, defaultOutboundYn, 'DELETE', initial.id);
  } else if (initial && isSelected && current) {
    if (isPickupDataModified(initial, current)) {
      return createPickupInfo(current, defaultOutboundYn, 'MODIFY', initial.id);
    }
  } else if (!initial && isSelected && current) {
    return createPickupInfo(current, defaultOutboundYn, 'NEW');
  }
  return null;
};

const generatePickupReleaseInfos = (
  filterData: TypeReceiveRequestDetailModalFilter,
  initialPickupInfos: TypeInitialPickupData
): TypePutReceivingRequestParams['pickupReleaseInfos'] => {
  const currentReleaseCode = filterData.releaseCode?.value;
  if (
    currentReleaseCode !== PICKUP_SHIPPING_TYPES.KURLY_PICKUP &&
    currentReleaseCode !== PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP
  ) {
    return [];
  }

  const pickupInfos: TypePutReceivingRequestParams['pickupReleaseInfos'] = [];
  const currentData = filterData.pickupAddressData;

  const hasKurlyData = Boolean(initialPickupInfos.kurlyDefault || initialPickupInfos.kurlyEtc);
  const hasMilkrunData = Boolean(
    initialPickupInfos.milkrunDefault || initialPickupInfos.milkrunEtc1 || initialPickupInfos.milkrunEtc2
  );
  const isReleaseCodeChanged =
    (hasKurlyData && currentReleaseCode === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP) ||
    (hasMilkrunData && currentReleaseCode === PICKUP_SHIPPING_TYPES.KURLY_PICKUP);

  if (isReleaseCodeChanged) {
    Object.entries(initialPickupInfos).forEach(([key, info]) => {
      if (info) {
        const defaultOutboundYn = key === 'kurlyDefault' || key === 'milkrunDefault';
        pickupInfos.push(createPickupInfo({} as TypePickupData, defaultOutboundYn, 'DELETE', info.id));
      }
    });
  }

  if (currentReleaseCode === PICKUP_SHIPPING_TYPES.KURLY_PICKUP) {
    const currentSelection = filterData.kurlyPickupAddress;
    const defaultInfo = processPickupAddressUpdate(
      initialPickupInfos.kurlyDefault,
      currentData?.kurlyDefault,
      currentSelection === 'default',
      true
    );
    const etcInfo = processPickupAddressUpdate(
      initialPickupInfos.kurlyEtc,
      currentData?.kurlyEtc,
      currentSelection === 'etc',
      false
    );
    if (defaultInfo) pickupInfos.push(defaultInfo);
    if (etcInfo) pickupInfos.push(etcInfo);
  } else if (currentReleaseCode === PICKUP_SHIPPING_TYPES.MILKRUN_PICKUP) {
    const currentSelections = filterData.milkrunPickupAddresses;
    const pickupMappings = [
      {
        initial: initialPickupInfos.milkrunDefault,
        current: currentData?.milkrunDefault,
        isSelected: currentSelections.default,
        defaultOutboundYn: true,
      },
      {
        initial: initialPickupInfos.milkrunEtc1,
        current: currentData?.milkrunEtc1,
        isSelected: currentSelections.etc1,
        defaultOutboundYn: false,
      },
      {
        initial: initialPickupInfos.milkrunEtc2,
        current: currentData?.milkrunEtc2,
        isSelected: currentSelections.etc2,
        defaultOutboundYn: false,
      },
    ];
    pickupMappings.forEach(({ initial, current, isSelected, defaultOutboundYn }) => {
      const info = processPickupAddressUpdate(initial, current, isSelected, defaultOutboundYn);
      if (info) pickupInfos.push(info);
    });
  }

  return pickupInfos;
};

const createParams = (
  orderId: number,
  filterData: TypeReceiveRequestDetailModalFilter,
  initialPickupInfos: TypeInitialPickupData,
  orderItems: TypePutReceivingRequestParams['items'],
  deletedItems: TypePutReceivingRequestParams['items']
): TypePutReceivingRequestParams => {
  return {
    id: orderId,
    releaseCode: filterData.releaseCode?.value as string,
    courier: filterData.courier,
    invoiceNo: filterData.invoiceNumber,
    ...(filterData.expectedTime && { dailyCapaId: Number(filterData.expectedTime) }),
    remarks: filterData.note,
    pickupReleaseInfos: generatePickupReleaseInfos(filterData, initialPickupInfos),
    items: [...orderItems, ...deletedItems],
  };
};

/**
 * [입고요청관리] 입고요청 수정 API
 */
const fetchPutReceivingRequest = async ({
  ownerCode,
  orderId,
  filterData,
  initialPickupData,
  tableData,
  deletedTableData,
}: UpdateRequestParams) => {
  const orderItems = tableData.filter(({ modificationType }) => modificationType).map(formatOrderItem);
  const deletedItems = deletedTableData.map(formatOrderItem);
  const params = createParams(orderId, filterData, initialPickupData, orderItems, deletedItems);

  const { data } = await axiosInstance.put<TypeApiResponse<TypePutReceivingRequestResponse>>(
    `/api-inbound/v1/3pl/receiving/${ownerCode}/request`,
    params
  );
  return data.data;
};

/**
 * [입고요청관리] 입고요청 수정 Mutation Hook
 * @description 에러 처리는 사용처에서 개별 처리 (특정 에러 코드에 따른 분기)
 */
export const usePutReceivingRequestMutation = () => {
  return useMutation({
    mutationFn: fetchPutReceivingRequest,
    meta: { showGlobalErrorToast: false },
  });
};

registerMockAPI<TypeApiResponse<TypePutReceivingRequestResponse>>({
  method: 'put',
  endpoint: '/api-inbound/v1/3pl/receiving/:ownerCode/request',
  label: '입고/입고요청/요청수정',
  scenarios: [
    {
      name: '정상 응답',
      response: { data: { orderId: 11422, modifiedItemIds: [48656, 48657], deletedItemIds: [] } },
    },
    {
      name: '입고가능물량 없음 (9040)',
      response: {
        error: {
          code: RECEIVE.RECEIVE_DAILY_CAPA_NOT_FOUND,
          message: '일별 입고 가능 물량 정보를 찾을 수 없습니다.',
        },
      },
      status: 400,
    },
    {
      name: '픽업 수정시간 초과 (9056)',
      response: {
        error: {
          code: RECEIVE.PICKUP_EDIT_TIME_EXCEEDED,
          message: '컬리/밀크런픽업 입고요청 정보는 입고예정일 전날 17시까지 수정가능합니다.',
        },
      },
      status: 400,
    },
    {
      name: '유효하지 않은 날짜값 (9017)',
      response: {
        error: {
          code: 9017,
          message: '유효하지 않은 날짜값입니다.',
          values: [1],
        },
      },
      status: 400,
    },
  ],
});
